import { capitalize } from "lodash";

import { consts } from "../../shim/jsbridge";
import { NetworkRepEntry } from "../network/@types/Url";
import { getSearchPivotLink } from "../samples/util";
import {
  Classification,
  ClassificationStatsDict,
  ItemWithClassification,
  PieCountsDroppedFiles,
  PieCountsRha1,
  RlNode
} from "./@types";

const colors: { [key: string]: string } = {
  malicious: "#d00000",
  suspicious: "#ff8000",
  goodware: "#3a852b",
  unknown: "#333333",
  no_threats_found: "#3a852b"
};

const mapUnwantedClassifications = (classification?: string) => {
  const classificationLower = classification?.toLowerCase() ?? "unknown";
  if (classificationLower === "unclassified") return "unknown";
  if (classificationLower === "known") return "goodware";
  if (classificationLower === "clean") return "unknown";
  if (classificationLower === "no_threats_found") return "goodware";
  return classificationLower as Classification;
};

interface CalculatePieChunkMapFn {
  (item: ItemWithClassification | RlNode): Classification;
}

// rlcs classifications "no_threats_found", "malicious", "suspicious" are mapped to a type below
// (no_threats_found is goodware for easier color mappings and icon mappings)
type MappedClassificationRlCloudSandbox =
  | "goodware"
  | "malicious"
  | "suspicious";

type RlSandboxClassificationStatsDict = {
  [key in MappedClassificationRlCloudSandbox]: number;
};
export const calculateDroppedFilesPieChunks = (
  data: ItemWithClassification[] | RlNode[],
  mapperFn: CalculatePieChunkMapFn
) => {
  const stats: RlSandboxClassificationStatsDict = {
    malicious: 0,
    goodware: 0,
    suspicious: 0
  };

  data?.forEach(item => {
    const classification = mapUnwantedClassifications(
      mapperFn(item)
    ) as MappedClassificationRlCloudSandbox;
    stats[classification] = (stats[classification] || 0) + 1;
  });

  const mapLabel = (classification: MappedClassificationRlCloudSandbox) =>
    classification === "goodware" ? "no_threats_found" : classification;

  return Object.keys(stats).map((key: MappedClassificationRlCloudSandbox) => ({
    value: stats[key],
    fill: colors[key],
    labela: mapLabel(key)
  }));
};

const extractExtractedFilesPieChunks = ({
  malicious = "0",
  goodware = "0",
  suspicious = "0",
  unknown = "0"
} = {}) => ({
  points: [
    {
      value: parseInt(malicious, 10),
      fill: colors.malicious,
      labela: "malicious"
    },
    {
      value: parseInt(goodware, 10),
      fill: colors.goodware,
      labela: "goodware"
    },
    {
      value: parseInt(suspicious, 10),
      fill: colors.suspicious,
      labela: "suspicious"
    },
    {
      value: parseInt(unknown, 10),
      fill: colors.unknown,
      labela: "unknown"
    }
  ]
});
const extractDroppedFilesPieChunks = ({
  malicious = 0,
  no_threats_found = 0,
  suspicious = 0
}: PieCountsDroppedFiles = {}) => ({
  points: [
    {
      value: malicious,
      fill: colors.malicious,
      labela: "malicious"
    },
    {
      value: suspicious,
      fill: colors.suspicious,
      labela: "suspicious"
    },
    {
      value: no_threats_found,
      fill: colors.goodware,
      labela: "No Threats Found"
    }
  ],
  total: suspicious + malicious + no_threats_found
});
const extractRhaPieChunks = ({
  malicious = 0,
  goodware = 0,
  suspicious = 0
}: PieCountsRha1 = {}) => ({
  points: [
    {
      value: malicious,
      fill: colors.malicious,
      labela: "malicious"
    },
    {
      value: goodware,
      fill: colors.goodware,
      labela: "goodware"
    },
    {
      value: suspicious,
      fill: colors.suspicious,
      labela: "suspicious"
    }
  ],
  extra: {
    total_count: goodware + suspicious + malicious
  },
  total: malicious + goodware + suspicious
});
const calculatePieChunks = (
  data: ItemWithClassification[] | RlNode[],
  mapperFn: CalculatePieChunkMapFn
) => {
  const stats: ClassificationStatsDict = {
    goodware: 0,
    malicious: 0,
    unknown: 0,
    suspicious: 0
  };

  data?.forEach(item => {
    const classification = mapUnwantedClassifications(mapperFn(item));
    stats[classification] = (stats[classification] || 0) + 1;
  });

  return Object.keys(stats).map((key: Classification) => ({
    value: stats[key],
    fill: colors[key],
    labela: key
  }));
};

type ChildrenClassificationCountDict =
  | { [key in Classification]: number }
  | Record<string, never>;
type ChildrenCountsDict = { [key: string]: ChildrenClassificationCountDict };

const calculateNodeChildren = (
  data: RlNode[],
  filetypeFn = (item: RlNode) => item.extra?.sample_type,
  classificationFn = (item: RlNode) => item.extra?.classification
) => {
  const fileTypeCounts = data.reduce((counts, item) => {
    const classification = mapUnwantedClassifications(classificationFn(item));
    const fileType = filetypeFn(item);
    if (!fileType) return [];
    if (!counts[fileType]) {
      counts[fileType] = {};
    }
    if (!counts[fileType][classification]) {
      counts[fileType][classification] = 0;
    }
    counts[fileType][classification] += 1;
    return counts;
  }, {} as ChildrenCountsDict);
  return Object.entries(fileTypeCounts).map(([fileType, counts]) => ({
    fileType,
    classifications: {
      ...counts
    }
  }));
};

const getNodeSize = (childrenCount: number) => {
  const defaultSize = 40;
  const ranges = [
    { min: 0, max: 10, nodeSize: 60 },
    { min: 11, max: 50, nodeSize: 70 },
    { min: 51, max: 200, nodeSize: 80 },
    { min: 201, max: 1000, nodeSize: 90 },
    { min: 1001, max: Infinity, nodeSize: 100 }
  ];

  const foundRange = ranges.find(
    range => childrenCount >= range.min && childrenCount <= range.max
  );

  return foundRange ? foundRange.nodeSize : defaultSize;
};

const getSearchLink = (type: string | undefined) => {
  if (!type || ["url", "domain", "ip"].includes(type)) return null;
  const typeField = type.replaceAll(" ", "");
  return getSearchPivotLink(`filetype:${typeField}`);
};

const formatChildrenGroupLabel = (label: string) => {
  if (label === "domain") return capitalize(label);
  if (["url", "ip"].includes(label)) return label.toUpperCase();
  return label;
};

const iconsPath = "/static/file_types-source-classification";

const KNOWN_NETWORK_REP_TYPES = new Set(["domain", "url", "ip"]);

const getNetworkReputationIcon = (item: NetworkRepEntry) => {
  const classification = mapUnwantedClassifications(item.classification);
  const defaultIcon = `${iconsPath}/default-${classification}.png`;
  const image = item.country
    ? `/static/flags/${item.country.toLowerCase()}.png`
    : KNOWN_NETWORK_REP_TYPES.has(item.type)
      ? `${iconsPath}/${item.type}-${classification}.png`
      : defaultIcon;
  return {
    image,
    brokenImage: defaultIcon
  };
};

// this is temp & should be done on backend once we have APIs for graph
const getFileIcon = (
  fileType?: string,
  fileSubType?: string,
  classification?: Classification,
  identification?: string
) => {
  const { filetypes, sample_identification_icons: identificationIcons } =
    consts;

  const filteredClassification = mapUnwantedClassifications(classification);

  const defaultIcon = `${iconsPath}/default-${filteredClassification}.png`;
  const brokenImageIcon = `/static/file_types-source-classification/default-${filteredClassification}.png`;
  // identification can have higher priority in some cases like .png
  if (identification) {
    const identificationLower = identification.toLowerCase();
    const iconName =
      identificationIcons[identificationLower] || identificationLower;
    // filenames are 3 letters long + some exceptions which we want to
    // try before looking into fileType & fileSubtype
    if (
      iconName?.length === 3 ||
      ["jpeg", "gzip", "android", "7zip", "win-sys", "mac-sys"].includes(
        iconName
      )
    ) {
      // might cause slowness in case svgs not found!
      return {
        image: `${iconsPath}/${iconName}-${filteredClassification}.png`,
        brokenImage: brokenImageIcon
      };
    }
  }
  // if no identification, try with filetypes
  if (!fileType)
    return {
      image: defaultIcon,
      brokenImage: brokenImageIcon
    };
  const type = fileType.toLowerCase();
  const subtype = fileSubType ? fileSubType.toLowerCase() : null;
  // @ts-ignore
  const iconName = filetypes[type]?.[subtype] || filetypes[type]?.["*"] || type;

  return {
    image: iconName
      ? `${iconsPath}/${iconName}-${filteredClassification}.png`
      : defaultIcon,
    brokenImage: brokenImageIcon
  };
};

/**
 * Shortens the url if it is longer than 20 characters
 * - if the url is not valid, returns the first 20 characters and 3 dots
 * - if the hostname is longer than 20 characters, returns the protocol and the first 20 characters of the hostname and 3 dots
 * - if the hostname is shorter than 20 characters and pathname is longer than 20 characters, returns the protocol, the hostname and 3 dots and last 20 characters of pathname
 * @param url - url to be shortened
 */
const shortenUrl = (url: string) => {
  let urlObject;
  try {
    urlObject = new URL(url);
  } catch (e) {
    return `${url.slice(0, 20)}...`;
  }

  if (urlObject.hostname.length > 20) {
    return `${urlObject.protocol}//${urlObject.hostname.slice(0, 20)}...`;
  }

  if (urlObject.pathname.length > 20) {
    return `${urlObject.protocol}//${
      urlObject.hostname
    }/...${urlObject.pathname.slice(-20)}`;
  }

  if (urlObject.search.length > 10) {
    return `${urlObject.protocol}//${urlObject.hostname}${
      urlObject.pathname
    }${urlObject.search.slice(0, 10)}...`;
  }

  return url;
};

/**
 * Generates label for nodes depending on the type of the node:
 * - ip and domain - returns the label
 * - sha1 - returns the first 7 and last 7 characters
 * - url - returns the first 20 characters and 3 dots if url is not valid
 * - url - returns the first 20 characters and 3 dots if the hostname is longer than 20 characters
 * - url - returns the protocol, the hostname and 3 dots and last 20 characters of pathname if the hostname is shorter than 20 characters and pathname is longer than 20 characters
 * - url - returns the protocol, the hostname, the pathname and 3 dots and first 10 characters of search if the hostname and pathname are shorter than 20 characters and search is longer than 10 characters
 * - everything else - if the label is longer than 20 characters, returns the first 20 characters and 3 dots, otherwise returns the label
 * @param label - label to be shortened
 * @param type - type of the node
 */
const generateLabel = (label: string, type: string) => {
  switch (type) {
    case "ip":
    case "domain":
      return label;
    case "url":
      return shortenUrl(label);
    case "sha1":
      return `${label.slice(0, 7)}...${label.slice(-7)}`;
    default:
      return label.length > 20 ? `${label.slice(0, 20)}...` : label;
  }
};

export {
  calculateNodeChildren,
  calculatePieChunks,
  colors,
  extractDroppedFilesPieChunks,
  extractExtractedFilesPieChunks,
  extractRhaPieChunks,
  formatChildrenGroupLabel,
  generateLabel,
  getFileIcon,
  getNetworkReputationIcon,
  getNodeSize,
  getSearchLink,
  mapUnwantedClassifications
};
